import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RiskScore } from '../components/RiskScore';
import { Warnings } from '../components/Warnings';
import { analyzeToken, type TokenAnalysis } from '../lib/api';
import { shortenAddress } from '../lib/utils';

/**
 * Protected Share Link page
 * Shows risk analysis before allowing users to proceed to the actual token page
 * This helps prevent clicking on potentially malicious token links
 */
export function SafeLink() {
  const { address } = useParams<{ address: string }>();
  const navigate = useNavigate();

  const [analysis, setAnalysis] = useState<TokenAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!address) return;

    setLoading(true);
    setError(null);

    analyzeToken(address)
      .then(setAnalysis)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [address]);

  // Auto-countdown for low risk tokens
  useEffect(() => {
    if (analysis && analysis.riskLevel === 'low' && countdown === null) {
      setCountdown(5);
    }
  }, [analysis, countdown]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleProceed = () => {
    if (address) {
      navigate(`/${address}`);
    }
  };

  const handleProceedToPump = () => {
    if (address) {
      window.open(`https://pump.fun/${address}`, '_blank', 'noopener,noreferrer');
    }
  };

  const copyLink = async () => {
    if (!address) return;
    // Use the API share endpoint for dynamic OG tags
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.skop.site';
    const smartLink = `${apiUrl}/share/${address}`;
    try {
      await navigator.clipboard.writeText(smartLink);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '48px', textAlign: 'center' }}>
        <div
          className="animate-spin"
          style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 16px',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
          }}
        />
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
          Analyzing Token Safety
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
          Please wait while we scan this token...
        </p>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div style={{ paddingTop: '48px', textAlign: 'center' }}>
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 16px',
          background: 'rgba(255, 59, 59, 0.1)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
        }}>
          ?
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
          Token Not Found
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '24px' }}>
          {error || 'Unable to analyze this token address'}
        </p>
        <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginBottom: '24px' }}>
          {address && shortenAddress(address, 8)}
        </p>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Back to Home
        </button>
      </div>
    );
  }

  const riskColor = {
    low: 'var(--success)',
    medium: 'var(--warning)',
    high: 'var(--error)',
    critical: 'var(--error)',
  }[analysis.riskLevel];

  const riskBg = {
    low: 'rgba(0, 255, 136, 0.1)',
    medium: 'rgba(255, 184, 0, 0.1)',
    high: 'rgba(255, 59, 59, 0.1)',
    critical: 'rgba(255, 59, 59, 0.15)',
  }[analysis.riskLevel];

  return (
    <div style={{ paddingTop: '16px', maxWidth: '480px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 12px',
          background: 'var(--accent-muted)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </div>
        <h1 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px' }}>
          Protected Link
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          Review the safety analysis before proceeding
        </p>
      </div>

      {/* Token Info Card */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          {analysis.image && (
            <img
              src={analysis.image}
              alt={analysis.name}
              style={{ width: '48px', height: '48px', borderRadius: '50%' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600 }}>{analysis.name}</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{analysis.symbol}</p>
          </div>
          <span style={{
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: 600,
            background: 'var(--accent-muted)',
            color: 'var(--accent)',
            borderRadius: 'var(--radius-sm)',
          }}>
            {analysis.platform}
          </span>
        </div>

        <p style={{
          fontSize: '11px',
          color: 'var(--text-tertiary)',
          fontFamily: 'var(--font-mono)',
          padding: '8px',
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-sm)',
          wordBreak: 'break-all',
        }}>
          {analysis.address}
        </p>
      </div>

      {/* Risk Score */}
      <div className="card" style={{
        marginBottom: '16px',
        background: riskBg,
        border: `1px solid ${riskColor}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600 }}>Risk Assessment</span>
          <span style={{
            padding: '4px 12px',
            fontSize: '12px',
            fontWeight: 700,
            background: riskColor,
            color: '#000',
            borderRadius: 'var(--radius-sm)',
            textTransform: 'uppercase',
          }}>
            {analysis.riskLevel}
          </span>
        </div>

        <RiskScore score={analysis.riskScore} level={analysis.riskLevel} />

        {analysis.warnings.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <Warnings warnings={analysis.warnings} />
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '8px',
        marginBottom: '24px',
      }}>
        <div className="card" style={{ padding: '12px' }}>
          <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Market Cap</p>
          <p style={{ fontSize: '14px', fontWeight: 600 }}>
            ${analysis.marketCap >= 1000000
              ? `${(analysis.marketCap / 1000000).toFixed(2)}M`
              : `${(analysis.marketCap / 1000).toFixed(1)}K`}
          </p>
        </div>
        <div className="card" style={{ padding: '12px' }}>
          <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Holders</p>
          <p style={{ fontSize: '14px', fontWeight: 600 }}>{analysis.holderCount.toLocaleString()}</p>
        </div>
        <div className="card" style={{ padding: '12px' }}>
          <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Top 5 Hold</p>
          <p style={{
            fontSize: '14px',
            fontWeight: 600,
            color: analysis.top5HolderPercent > 50 ? 'var(--error)' : 'var(--text-primary)',
          }}>
            {analysis.top5HolderPercent.toFixed(1)}%
          </p>
        </div>
        <div className="card" style={{ padding: '12px' }}>
          <p style={{ fontSize: '10px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Creator Tokens</p>
          <p style={{
            fontSize: '14px',
            fontWeight: 600,
            color: analysis.creatorTokenCount > 5 ? 'var(--warning)' : 'var(--text-primary)',
          }}>
            {analysis.creatorTokenCount}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {analysis.riskLevel === 'critical' && (
          <div style={{
            padding: '12px',
            background: 'rgba(255, 59, 59, 0.1)',
            border: '1px solid var(--error)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '8px',
          }}>
            <p style={{ fontSize: '12px', color: 'var(--error)', fontWeight: 500 }}>
              This token has critical risk indicators. Proceed with extreme caution.
            </p>
          </div>
        )}

        <button
          onClick={handleProceed}
          className="btn btn-primary"
          style={{
            padding: '14px',
            fontSize: '14px',
          }}
        >
          View on SKōP {countdown !== null && countdown > 0 ? `(${countdown})` : ''}
        </button>

        <button
          onClick={handleProceedToPump}
          className="btn btn-secondary"
          style={{
            padding: '12px',
            fontSize: '13px',
          }}
        >
          Open on pump.fun
        </button>

        <button
          onClick={copyLink}
          style={{
            padding: '10px',
            fontSize: '12px',
            background: 'transparent',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
          }}
        >
          Copy Safe Link
        </button>
      </div>

      {/* Footer */}
      <p style={{
        textAlign: 'center',
        fontSize: '11px',
        color: 'var(--text-tertiary)',
        marginTop: '24px',
      }}>
        Protected by SKōP - Always verify before you trade
      </p>
    </div>
  );
}
